import "./App.css";

import MenuIcon from "@mui/icons-material/Menu";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useEffect } from "react";
import {
  datasourceIdFromPath,
  previewIdFromLocation,
  setActivePreviewMemberId,
} from "./lib/preview-as";
import { usePreviewSession } from "./lib/preview-session";
import {
  HashRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

import Nav from "./components/Nav";
import CompactChrome from "./components/CompactChrome";
import { useDatasources } from "./lib/useDatasources";
import {
  shouldShowAppSidebar,
  shouldShowCompactChrome,
} from "./lib/app-sidebar";
import Collection from "./pages/Collection";
import Dashboard from "./pages/Dashboard";
import DataSources from "./pages/DataSources";
import SheetsConnect from "./pages/SheetsConnect";
import AcceptInvite from "./pages/AcceptInvite";
import Members from "./pages/Members";
import Activity from "./pages/Activity";
import Settings from "./pages/Settings";
import Auth from "./pages/auth";
import OauthCallback from "./pages/auth/oauth";
import SheetsCallbackBanner from "./components/SheetsCallbackBanner";
import VerifyEmail from "./pages/auth/verify-email";
import Guest from "./pages/guest";
import Setup from "./pages/setup";
import { useAuth } from "./lib/useAuth";
import { keepsRouterWhileLoading } from "./lib/boot-route";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { createAppTheme } from "./lib/theme";
import { ThemeModeProvider, useThemeMode } from "./lib/theme-mode";
import { Button } from "./components/ui/Button";

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds - data is considered fresh for this duration
      refetchOnWindowFocus: true, // Refetch when window regains focus
      retry: 1, // Retry failed requests once
    },
  },
});

function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const { fetchDatasources, setDatasources } = useDatasources();

  useEffect(() => {
    if (isAuthenticated) {
      fetchDatasources();
    } else {
      setDatasources([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Guest/setup auto-login and the /auth buttons set isLoading. Unmounting the
  // router here would tear down the page that is still waiting to navigate.
  const keepRouterWhileLoading = keepsRouterWhileLoading(window.location.hash);

  if (isLoading && !keepRouterWhileLoading) {
    return (
      <div role="status" className="flex min-h-screen items-center justify-center gap-3 bg-od-bg p-4">
        <span aria-hidden="true" className="h-5 w-5 shrink-0 rounded-full border-2 border-od-border border-t-od-primary motion-safe:animate-spin" />
        <p className="text-sm text-od-text">
          Loading your data...
        </p>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <ThemedApp isAuthenticated={isAuthenticated} />
      </ThemeModeProvider>
    </QueryClientProvider>
  );
}

/**
 * Declared at module scope on purpose. Defining it inside `App` produced a new
 * component type on every App render, which remounted the entire routed tree
 * (and every DataGrid inside it) whenever auth state changed.
 */
const ThemedApp = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
    const { resolvedMode } = useThemeMode();
    return (
      <ThemeProvider theme={createAppTheme(resolvedMode)}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
          <Router>
            <AppShell isAuthenticated={isAuthenticated} />
          </Router>
        </LocalizationProvider>
        <Toaster
          position="top-center"
          containerClassName="od-toaster"
          toastOptions={{
            style: {
              background: 'var(--od-surface)',
              color: 'var(--od-text)',
              border: '1px solid var(--od-border)',
              fontSize: '0.8125rem',
              pointerEvents: 'none',
            },
          }}
        />
        {/* bottom-left: the default corner sits on top of the grid pagination. */}
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </ThemeProvider>
    );
};

/**
 * Guest/setup own the first-paint chrome (CollectionSkeleton). Hide the real
 * Nav while those routes are up so the ghost sidebar is not doubled after
 * loginAsGuest flips isAuthenticated, before pathAfterAuth navigates away.
 *
 * Also hide the sidebar on the Sheets wizard, on /auth, and when the user has
 * no own (non-demo) source — unless they are already on the sample they opted
 * into (or opened via a direct demo link).
 */
const AppShell = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const location = useLocation();
  const { token } = useAuth();
  const previewMemberId = previewIdFromLocation(location);
  const previewDatasourceId = datasourceIdFromPath(location.pathname);
  // Set this before children fetch, so a data read in the same turn carries the id.
  setActivePreviewMemberId(previewMemberId);
  const { datasources, isInitialLoad } = useDatasources();

  useEffect(() => {
    if (!previewMemberId || !previewDatasourceId) {
      usePreviewSession.getState().clear();
      return;
    }
    void usePreviewSession
      .getState()
      .load(previewDatasourceId, previewMemberId);
  }, [previewMemberId, previewDatasourceId, token]);
  const showSidebar = shouldShowAppSidebar({
    isAuthenticated,
    pathname: location.pathname,
    datasources,
    isInitialLoad,
  });
  const showCompactChrome = shouldShowCompactChrome({
    isAuthenticated,
    pathname: location.pathname,
    showSidebar,
    isInitialLoad,
  });

  return (
    <div className={showSidebar ? "flex h-screen overflow-hidden" : ""}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[1400] focus:rounded-md focus:bg-od-surface focus:px-4 focus:py-3 focus:text-od-primary-text focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-od-primary"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
      >
        Skip to main content
      </a>
      {showSidebar && <Nav />}
      <main
        id="main-content"
        tabIndex={-1}
        className={
          showSidebar
            ? "flex-1 overflow-auto min-w-0 flex flex-col"
            : "w-full"
        }
      >
        {showCompactChrome && <CompactChrome />}
        {showSidebar && (
          <div
            className="md:hidden sticky top-0 z-40 flex items-center gap-3 px-4 bg-od-surface border-b border-od-border"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingLeft: "max(1rem, env(safe-area-inset-left))",
              paddingRight: "max(1rem, env(safe-area-inset-right))",
              minHeight: "calc(3.5rem + env(safe-area-inset-top))",
            }}
          >
            <Button
              variant="secondary"
              size="icon"
              className="bg-od-surface"
              onClick={() => {
                const event = new CustomEvent("toggle-mobile-menu");
                window.dispatchEvent(event);
              }}
              title="Toggle menu"
              aria-label="Toggle menu"
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </Button>
            <span className="text-sm font-semibold text-od-text truncate">
              {location.pathname === "/settings" ? (
                "Settings"
              ) : (
                <a href="https://onlydash.io" className="inline-flex min-h-11 min-w-11 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-surface">OnlyDash</a>
              )}
            </span>
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold tracking-wide bg-od-primary/15 text-od-primary-text border border-od-primary/30"
              data-testid="beta-label"
            >
              Beta
            </span>
          </div>
        )}
        <div
          className={
            showSidebar ? "flex min-h-0 flex-1 flex-col p-3 md:p-4" : ""
          }
        >
          <SheetsCallbackBanner />
          <Routes>
            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <DataSources />
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/data-sources"
              element={
                isAuthenticated ? (
                  <DataSources />
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            {/*
              MOSA-364. The hand-off contract is
              https://app.onlydash.io/#/connect/sheets?sheet=<encoded URL>.
              Public on purpose: a guest lands here first and signs up from
              step 2, with the link kept. Never redirects away.
            */}
            <Route path="/connect/sheets" element={<SheetsConnect />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/oauth" element={<OauthCallback />} />
            <Route path="/guest" element={<Guest />} />
            <Route path="/setup" element={<Setup />} />
            {/*
              MOSA-694 item 6: the screen shows a confirmation after a
              successful verify, and a successful verify is what signs the user
              in. Guarding the route on isAuthenticated here unmounted the
              screen before the confirmation could render, so VerifyEmail owns
              the same redirect itself now.
            */}
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            <Route
              path="/settings"
              element={
                isAuthenticated ? (
                  <Settings />
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            <Route
              path="/datasources/:datasourceId/dashboards/:id"
              element={
                isAuthenticated ? (
                  <Dashboard />
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            {/* Share (MOSA-367 slice 1). Only the owner has anything to do
                here; the API answers 403 to a member on every route this
                page calls, and the page says so. */}
            <Route
              path="/datasources/:datasourceId/members"
              element={
                isAuthenticated ? <Members /> : <Navigate to="/auth" replace />
              }
            />
            {/* Activity (MOSA-368 slice 1). The owner's list of every change
                to this panel. The API answers 403 to a member, and the page
                says so. */}
            <Route
              path="/datasources/:datasourceId/activity"
              element={
                isAuthenticated ? <Activity /> : <Navigate to="/auth" replace />
              }
            />
            <Route
              path="/datasources/:datasourceId/collections/:collectionName"
              element={
                isAuthenticated ? (
                  <Collection />
                ) : (
                  <Navigate to="/auth" replace />
                )
              }
            />
            {/* Public: the invitee may not be signed in yet, and the page
                itself sends them to /auth while keeping the token. */}
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default App;
